import jwt from "jsonwebtoken";
import * as types from "./actionTypes";
import API from "../../services/UserService";
import {JWT_PUBLIC_KEY} from "../../components/common/Const";
import {apiCallError, beginApiCall} from "./apiStatusActions";
import { getStoredAuthToken, removeAuthToken } from "../../services/authTokenStorage";

const SUPPORTED_AUTH_ROLES = ["ADMIN", "OPERATOR", "TEACHER", "CLIENT"];

function normalizeRole(value) {
  if (!value) return null;
  const normalized = String(value).toUpperCase();
  return SUPPORTED_AUTH_ROLES.includes(normalized) ? normalized : null;
}

function normalizeRoles(values) {
  if (!Array.isArray(values)) return [];
  return [...new Set(values.map(normalizeRole).filter(Boolean))];
}

function normalizeAuthUser(decoded = {}) {
  const role = normalizeRole(decoded.role) || (
    Array.isArray(decoded.roles) && decoded.roles.length === 1
      ? normalizeRole(decoded.roles[0])
      : null
  );

  return {
    id: decoded.id,
    email: decoded.email || decoded.id,
    role,
    teacherId: decoded.teacherId || null,
    clientId: decoded.clientId || null,
    schoolId: decoded.schoolId || null,
    activeContextKey: decoded.activeContextKey || null,
    canSwitchContext: decoded.canSwitchContext || false,
    ...(normalizeRoles(decoded.systemRoles).length > 0 ? { systemRoles: normalizeRoles(decoded.systemRoles) } : {}),
  };
}

export function signInSuccess(user) {
  return { type: types.SIGN_IN_SUCCESS, user };
}

export function authFlowStepSuccess() {
  return { type: types.AUTH_FLOW_STEP_SUCCESS };
}

export function getLoginContextsSuccess() {
  return { type: types.GET_LOGIN_CONTEXTS_SUCCESS };
}

export function signIn(userCredentials) {
  // eslint-disable-next-line no-unused-vars
  return (dispatch, getState) => {
    dispatch(beginApiCall('signIn'));
    return API.signIn(userCredentials)
      .then((userLoggedIn) => {
        if (userLoggedIn && userLoggedIn.status) {
          dispatch(authFlowStepSuccess());
          return userLoggedIn;
        }
        dispatch(signInSuccess(userLoggedIn));
        return userLoggedIn;
      })
      .catch((error) => {
        dispatch(apiCallError(error));
        throw error;
      });
  };
}

export function signInWithGoogle(googleToken) {
  return (dispatch) => {
    dispatch(beginApiCall("signInWithGoogle"));
    return API.signInWithGoogle(googleToken)
      .then((userLoggedIn) => {
        if (userLoggedIn && userLoggedIn.status) {
          dispatch(authFlowStepSuccess());
          return userLoggedIn;
        }
        dispatch(signInSuccess(userLoggedIn));
        return userLoggedIn;
      })
      .catch((error) => {
        dispatch(apiCallError(error));
        throw error;
      });
  };
}

function apiOnlyAction(apiName, apiCall) {
  return (dispatch) => {
    dispatch(beginApiCall(apiName));
    return apiCall()
      .catch((error) => {
        dispatch(apiCallError(error));
        throw error;
      });
  };
}

export function signUpWithPassword(payload) {
  return apiOnlyAction("signUpWithPassword", () => API.signUpWithPassword(payload));
}

export function confirmEmailVerification(token) {
  return apiOnlyAction("confirmEmailVerification", () => API.confirmEmailVerification(token));
}

export function resendEmailVerification(email) {
  return apiOnlyAction("resendEmailVerification", () => API.resendEmailVerification(email));
}

export function selectLoginContext(contextChoiceToken, contextKey) {
  return (dispatch) => {
    dispatch(beginApiCall("selectLoginContext"));
    return API.selectLoginContext(contextChoiceToken, contextKey)
      .then((userLoggedIn) => {
        dispatch(signInSuccess(userLoggedIn));
        return userLoggedIn;
      })
      .catch((error) => {
        dispatch(apiCallError(error));
        throw error;
      });
  };
}

export function getLoginContexts() {
  return (dispatch) => {
    dispatch(beginApiCall("getLoginContexts"));
    return API.getLoginContexts()
      .then((contextChoice) => {
        dispatch(getLoginContextsSuccess());
        return contextChoice;
      })
      .catch((error) => {
        dispatch(apiCallError(error));
        throw error;
      });
  };
}

export function completeSsoProfile(profileCompletionToken, details) {
  return (dispatch) => {
    dispatch(beginApiCall("completeSsoProfile"));
    return API.completeSsoProfile(profileCompletionToken, details)
      .then((userLoggedIn) => {
        dispatch(signInSuccess(userLoggedIn));
        return userLoggedIn;
      })
      .catch((error) => {
        dispatch(apiCallError(error));
        throw error;
      });
  };
}

export function signOut() {
  removeAuthToken();
  return { type: types.SIGN_OUT };
}

export function checkToken() {
  return (dispatch) => {
    const token = getStoredAuthToken();
    if (token) {
      const secret = JWT_PUBLIC_KEY;
      jwt.verify(token, secret, {algorithms: ['RS256']}, (err, decoded) => {
        if (err) {
          // eslint-disable-next-line no-console
          console.log('token error', err);
          dispatch(signOut());
        }

        if (decoded) {
          // console.log('token found and verified', decoded);
          dispatch(signInSuccess(normalizeAuthUser(decoded)));
        }
      });
    }
  };
}
